import { Dialog } from '@material-ui/core';
import { DialogCloseComponent, Loading } from 'Components';
import { fibaBankUrl } from 'Config';
import { useInterval, useLocalization } from 'Hooks';
import { FC, useRef, useState } from 'react';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  checkPaymentStatusRequest,
  close3DSecureModal,
  creditCardTransactionsStore,
  masterpass3DFlowCompleted,
} from 'Stores/CreditCardTransactions';
import { checkOnmicroCreditStatus, microCreditStore } from 'Stores/MicroCredit';
import { showToastify } from 'Stores/Toastify';
import { userStore } from 'Stores/User';

import './Modal3DSecure.scss';

/** 3D akışının döneceği kendi sayfamızın yolu — `public/masterpass-3d-return.html`. */
const MASTERPASS_3D_RETURN_PATH = '/masterpass-3d-return.html';

type Modal3DSecureProps = {
  link: string;
  open: boolean;
  paymentNo?: string;
  close: () => void;
  isFibabank?: boolean;
};

const Modal3DSecure: FC<Modal3DSecureProps> = ({ link, open, close, isFibabank }) => {
  const { msg_timeout } = useLocalization();

  const dispatch = useDispatch();
  const userData = useSelector(userStore);
  const microCreditData = useSelector(microCreditStore);
  const creditStatus = microCreditData?.creditStatus;
  const customerNumber = userData?.customerNumber;
  const sessionToken = userData?.sessionToken;
  const orderId = microCreditData?.orderId;
  const orderStatus = microCreditData?.orderStatus;

  const [checkOverTime, setCheckOverTime] = useState<boolean>(true);

  const { paymentStatus, activePayment } = useSelector(creditCardTransactionsStore);

  // ⚠️ `createRef` yerine `useRef`: `createRef` her render'da YENİ bir ref üretir,
  // dolayısıyla `onLoad` içinden iframe'e güvenilir biçimde erişilemiyordu.
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  const isLinkIncludeHtml = link?.includes('<html');

  const [loading, setLoading] = useState(true);

  // Limit to request for 3 minutes

  const [remainingSeconds, setRemainingSeconds] = useState<number>(isFibabank ? 1200 : 180);

  useInterval(() => {
    if (remainingSeconds > 0) {
      setRemainingSeconds(remainingSeconds - 1);
    }
  }, 1000);

  useEffect(() => {
    if (remainingSeconds === 0) {
      setCheckOverTime(false);
      dispatch(close3DSecureModal());
      dispatch(showToastify({ toastMessage: msg_timeout, type: 'error' }));
      setRemainingSeconds(isFibabank ? 1200 : 180);
    } // eslint-disable-next-line
  }, [remainingSeconds]);

  useEffect(() => {
    if (checkOverTime) {
      setTimeout(() => {
        if (!activePayment) return;

        // ⚠️ Ödeme tamamlama çağrısı BURADAN YAPILMAZ.
        //
        // Bir ara denemede tamamlama bu yoklamaya iliştirilmişti; 3D bitmeden de
        // ateşlendiği için servis `TOKEN_IS_NOT_VALID` döndürüyordu ve 3D
        // başarıyla bittikten SONRAKİ çağrı da aynı hatayı alıyordu — yani
        // erken deneme akış token'ını geçersiz kılıyor. Tamamlama artık yalnızca
        // 3D'nin bittiği KESİN olarak bilindiğinde, dönüş sayfasının sinyaliyle
        // bir kez çağrılıyor (bkz. `masterpass3DFlowCompleted`).
        dispatch(
          checkPaymentStatusRequest({
            CustomerNumber: customerNumber,
            SessionToken: sessionToken,
            PaymentNumber: activePayment,
          })
        );
      }, 5000);
    }
  }, [paymentStatus, activePayment]);

  useEffect(() => {
    if (checkOverTime) {
      setTimeout(() => {
        orderId &&
          fibaBankUrl &&
          dispatch(
            checkOnmicroCreditStatus({
              CustomerNumber: customerNumber,
              SessionToken: sessionToken,
              OrderId: orderId,
            })
          );
      }, 5000);
    }
  }, [orderStatus, orderId]);

  const onLoadFrame = (obj: any) => {
    setTimeout(() => {
      setLoading(false);
    }, 3000);

    // ⚠️ İkinci yakalama yolu — dönüş sayfasının JS'i ÇALIŞMASA BİLE devreye girer.
    //
    // Masterpass dönüş adresine `POST` atıyor (gövdede `isSuccess`, `amount`,
    // `errorMessage` alanları geliyor). Dönüş sayfamız statik olarak sunulduğu için
    // POST'a cevap veremiyor ve tarayıcı bir hata sayfası gösteriyor — bu durumda
    // sayfadaki `postMessage` hiç çalışmıyor.
    //
    // Ama o hata sayfası da BİZİM origin'imizden döndüğü için iframe artık
    // same-origin: adresi okuyabiliyoruz. İhtiyacımız olan `responseCode` zaten
    // query string'de (`?responseCode=0000&token=...`), gövdeye gerek yok.
    //
    // Cross-origin adımlarda (banka/ACS sayfaları) bu okuma exception fırlatır;
    // beklenen durum olduğu için sessizce yutuluyor.
    try {
      const href = iframeRef.current?.contentWindow?.location?.href;
      if (href && href.includes(MASTERPASS_3D_RETURN_PATH)) {
        const params = new URL(href).searchParams;
        // ⚠️ Buradaki `token` AKIŞ TOKEN'I DEĞİL. Masterpass 3D bitiminde yeni bir
        // token üretip dönüş adresine koyuyor; `mp-3d-bank-success`'teki değerden
        // farklı. Tamamlama çağrısı bu değeri istiyor olabilir, o yüzden taşınıyor.
        dispatch(
          masterpass3DFlowCompleted({
            responseCode: params.get('responseCode') ?? '',
            token: params.get('token') ?? undefined,
          })
        );
      }
    } catch (error) {
      // Cross-origin adım — normal.
    }
  };

  /**
   * 3D akışının bitişini yakalar.
   *
   * ⚠️ Banka/ACS sayfaları cross-origin olduğu için iframe'in nereye gittiği
   * okunamıyor; akışın bittiği ancak kendi dönüş sayfamızın (`returnUrl` olarak
   * verilen `public/masterpass-3d-return.html`) gönderdiği mesajla anlaşılıyor.
   *
   * ⚠️ Mesaj kaynağı DOĞRULANIYOR: yalnızca kendi origin'imizden ve beklenen
   * `source` etiketiyle gelenler kabul edilir. Aksi hâlde iframe'deki herhangi
   * bir üçüncü taraf sayfa ödeme akışını tetikleyebilirdi.
   */
  useEffect(() => {
    const handleReturnMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.source !== 'masterpass-3d-return') return;

      dispatch(
        masterpass3DFlowCompleted({
          responseCode: event.data?.responseCode ?? '',
          token: event.data?.token || undefined,
        })
      );
    };

    window.addEventListener('message', handleReturnMessage);
    return () => window.removeEventListener('message', handleReturnMessage);
  }, [dispatch]);

  return (
    <Dialog open={open} onClose={close} keepMounted={false} id="modal3DSecure" disableBackdropClick>
      <div style={{ paddingBottom: 0 }} className="dialogContainer">
        <DialogCloseComponent closeFunction={close} />
        {loading && <Loading />}
        {/*
          ⚠️ `sandbox` özniteliği KASITLI OLARAK YOK.

          3D akışı tek bir sayfa değil, birbirine POST eden uzun bir zincir:
          ACS → banka (`est3dgate`, `MerchantServer`) → `mp-3d-bank-success` →
          üye işyerinin dönüş adresi. Sandbox listesi ne kadar gevşetilirse
          gevşetilsin zincirin son adımı Chrome tarafından düşürülüyordu:
          "CORS error" + 0 bayt, iframe `chrome-error://chromewebdata` sayfasına
          düşüyor ve dönüş sinyali hiç gelmiyordu. Aynı hata hem kendi dönüş
          sayfamızda hem de JWT servisinin verdiği adreste görüldü — yani sorun
          adreste değil, sandbox'tan kaynaklanıyordu.

          Buraya yalnızca Masterpass/banka 3D sayfaları yükleniyor (adres
          servisten geliyor, kullanıcı girdisiyle oluşturulmuyor); ödeme
          entegrasyonlarında bu iframe'in sandbox'suz olması olağan.
        */}
        <iframe
          id="frame"
          ref={iframeRef}
          onLoad={onLoadFrame}
          srcDoc={isLinkIncludeHtml ? link : undefined}
          src={!isLinkIncludeHtml ? link : undefined}
        />
      </div>
    </Dialog>
  );
};

export default Modal3DSecure;
